import { formatSingaporeDate } from '../../../../utils/timezone';
import type { LegacyPriceSources, PriceRange } from '../../../../types/priceSources';

type CostPriceDetails = NonNullable<LegacyPriceSources['cost_price_details']>;
type HistoricalData = NonNullable<LegacyPriceSources['historical_data']>;

/** What every tooltip section is drawn from. */
export interface PriceSourceContext {
  details: CostPriceDetails;
  historicalData: HistoricalData | null | undefined;
  /** The price research failed (source_type or fallback_level 'error'). */
  isErrorCase: boolean;
  /**
   * A low-confidence history was passed over for market research: the
   * history box is dimmed and marked "reference only", and the search
   * results are marked as the price used.
   */
  serpWasPreferred: boolean;
  showHistoricalDetails: boolean;
  toggleHistoricalDetails: () => void;
}

// The tooltip wrapper in CostPriceCell is transparent, so this card supplies its
// own frame; without it the card floated borderless over the table.
export const TOOLTIP_CARD_SX = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(16, 24, 40, 0.16), 0 1px 3px rgba(16, 24, 40, 0.08)",
} as const;

export const getConfidenceColor = (level?: string | null) => {
  if (!level) return 'text.secondary';

  switch (level.toLowerCase()) {
    case 'high':
      return 'success.main';
    case 'medium':
      return 'warning.main';
    case 'low':
    case 'very_low':
      return 'error.main';
    default:
      return 'text.secondary';
  }
};

export const formatPriceRange = (range?: PriceRange | null) => {
  if (!range || typeof range.min !== 'number' || typeof range.max !== 'number' || typeof range.count !== 'number') {
    return 'Price range unavailable';
  }

  try {
    return `$${range.min.toFixed(2)} - $${range.max.toFixed(2)} (from ${range.count} sources)`;
  } catch {
    return 'Price range unavailable';
  }
};

export const formatDate = (dateString?: string | null) => {
  if (!dateString || typeof dateString !== 'string') return 'Date unavailable';

  try {
    return formatSingaporeDate(dateString);
  } catch {
    return 'Date unavailable';
  }
};

export const safeNumber = (value?: number | null, decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) return '0.00';
  try {
    return value.toFixed(decimals);
  } catch {
    return '0.00';
  }
};
